import type { ReactNode } from 'react';

import Icon from '../Icon';

import './ChoiceCard.scss';

/** Пропсы карточки выбора ChoiceCard. */
export interface ChoiceCardProps {
  /** выбрана: рамка $primary 2px, серый фон, иконка залита $primary */
  selected?: boolean;
  /** добавлена (в расчёт/набор): рамка $success 2px, цена и действие — зелёные */
  added?: boolean;
  /** недоступна (нет цены): пунктирная рамка, серый текст, кнопка disabled */
  unavailable?: boolean;
  onClick?(): void;
  /** compact — мини-плитка в одну строку (радиусы); service — высокая карточка с ценой */
  variant?: 'default' | 'compact' | 'service';
  /** контент квадрата-иконки: буква или <Icon/>; при selected заливается $primary */
  icon?: ReactNode;
  /** заголовок (strong) */
  title?: ReactNode;
  /** подпись (small) под заголовком; пустая строка сохраняет отступ исходной вёрстки */
  note?: ReactNode;
  /** иконка и текст в одну строку (по умолчанию — колонкой) */
  inline?: boolean;
  /** цена нижнего ряда: 18px, $primary (added → $success) */
  price?: ReactNode;
  /** подпись (small) под ценой */
  priceNote?: ReactNode;
  /** приглушить цену (text-muted) — вариант «Нет цены» */
  priceMuted?: boolean;
  /** действие справа в нижнем ряду («+ Добавить»); при added — зелёное */
  action?: ReactNode;
  /** чекбокс слева от текста, синхронный с selected — для плиток мультивыбора
   *  (офисы/типы авто/диаметры). Стилизованный квадрат: нативный input внутрь
   *  кнопки вкладывать нельзя */
  checkbox?: boolean;
  /** доп. контент в конец текстового блока (бейджи и т.п.) */
  children?: ReactNode;
  className?: string;
}

/**
 * Карточка выбора из набора плиток: кликабельная кнопка с состояниями
 * selected / added / unavailable и готовой анатомией (иконка, заголовок,
 * подпись, цена, действие). Выделено из мастера ШМ (tfw-choice-card) в
 * коллекцию ui. Сетка — ChoiceGrid.
 */
const ChoiceCard = (props: ChoiceCardProps) => {
  const {
    selected, added, unavailable, onClick,
    variant = 'default', icon, title, note, inline,
    price, priceNote, priceMuted, action, checkbox,
    children, className,
  } = props;

  // small рендерится и с пустой строкой — сохранены отступы исходной вёрстки
  const hasNote = note !== undefined && note !== null;
  const hasText = Boolean(title) || hasNote || children;
  const textBlock = hasText && (
    <span className='choice-text'>
      { title && <strong>{ title }</strong> }
      { hasNote && <small>{ note }</small> }
      { children }
    </span>
  );

  const hasFooter = Boolean(price || priceNote || action);
  const checkBox = checkbox && (
    <span className={ `choice-check${ selected ? ' checked' : '' }` } aria-hidden='true'>
      { selected ? <Icon name='check' /> : null }
    </span>
  );

  return (
    <button
      type='button'
      disabled={ unavailable }
      onClick={ onClick }
      className={ [
        'choice-card',
        variant !== 'default' ? `choice-card-${ variant }` : '',
        selected ? 'selected' : '',
        added ? 'added' : '',
        unavailable ? 'unavailable' : '',
        className || '',
      ].filter(Boolean).join(' ') }
    >
      { variant === 'compact'
        ? (
          <span className='choice-compact-row'>
            { checkBox }
            { children ?? title }
          </span>
        )
        : (
          <>
            { inline || checkbox
              ? (
                <span className='choice-head'>
                  { icon && <span className='choice-icon'>{ icon }</span> }
                  { checkBox }
                  { textBlock }
                </span>
              )
              : (
                <>
                  { icon && <span className='choice-icon'>{ icon }</span> }
                  { textBlock }
                </>
              ) }
            { hasFooter && (
              <span className='choice-footer'>
                { (price || priceNote) && (
                  <span>
                    { price && (
                      <span className={ `choice-price${ priceMuted ? ' text-muted' : '' }` }>
                        { price }
                      </span>
                    ) }
                    { priceNote && <small>{ priceNote }</small> }
                  </span>
                ) }
                { action && <span className='choice-action'>{ action }</span> }
              </span>
            ) }
          </>
        ) }
    </button>
  );
};

export default ChoiceCard;
